/* eslint-disable max-len,object-curly-newline,camelcase */
import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  paymentFields as formControls,
  requiredField,
  validEmail,
} from '../../helpers/pressReleaseConstants';
import LoadingScreen from '../../../common/LoadingScreen';
import { checkoutURL } from '../helpers/divewireConstants';
import BillingContactInfo from '../../../common/BillingContactInfo';
import DisclaimerAndSubmitButton from '../components/DisclaimerAndSubmitButton';
import PaymentInfo from '../components/PaymentInfo/PaymentInfo';
import { useFormControls } from '../../../common/hooks/useFormControls';
import useStripeValidations from '../../../common/hooks/useStripeValidations';
import PriceSummaryBox from '../components/PriceSummaryBox';
import NonFieldErrorBanner from '../components/NonFieldErrorBanner';
import PaymentIntentService from '../services/paymentIntentService';
import displayBackendErrors from '../../../common/helpers/displayBackendErrors';
import getPriceSummaryValues from '../helpers/getPriceSummaryValues';
import EventsHelper from '../helpers/eventsHelper';
import IntroSection from '../../common/IntroSection';
import MainSection from '../components/MainSection';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';
import PRESS_RELEASE_STATUS from '../helpers/pressReleaseUtils';

const PaymentScreen = () => {
  const { pressRelease, pressReleaseMetadata, promoteSites, nonFieldError, setNonFieldError } = useOutletContext();
  const navigate = useNavigate();
  const { token } = useParams();
  if (pressRelease?.status === PRESS_RELEASE_STATUS.PAID) {
    navigate(`${checkoutURL}/${token}/confirmation`);
    return null;
  }
  const {
    fields, setField, setValidators, validateField, validateForm, resetForm, getErroringFieldNames,
  } = useFormControls(formControls);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const paymentIntentApi = new PaymentIntentService(token);
  const eventsHelper = new EventsHelper(pressRelease, pressReleaseMetadata, promoteSites.sitesList);
  const {
    selectedSites,
    numPressReleases,
    hasMultimediaAddons,
    totalPrice,
  } = getPriceSummaryValues(pressRelease, pressReleaseMetadata, promoteSites.sitesList);

  // STRIPE PAYMENT HANDLING //
  const {
    cardFields,
    handleCardNumberChange,
    handleCardExpiryChange,
    handleCardCVCChange,
    validatePaymentInfo,
    confirmCardPayment,
  } = useStripeValidations();

  // once we have a valid payment info trigger the add_payment_info event
  useEffect(() => {
    const paymentInfoIsValid = cardFields.cardNumber.completed && cardFields.cardExpiry.completed && cardFields.cardCVC.completed;
    if (paymentInfoIsValid) {
      eventsHelper.handleAddPaymentInfo();
    }
  }, [cardFields]);

  // Handles the display of the additional billing contact info fields
  const [billingContactInfoNeeded, setBillingContactInfoNeeded] = useState(false);
  const setBillingContactInfoNeededOnChange = (event) => {
    setBillingContactInfoNeeded(event.target.value === 'true');
    if (event.target.value === 'true') {
      setValidators('billingContactName', [requiredField]);
      setValidators('billingContactEmail', [requiredField, validEmail]);
    } else resetForm();
  };

  const handleSubmitClick = async () => {
    setIsSubmitting(true);
    const contactInfoIsValid = validateForm();
    const paymentInfoIsValid = validatePaymentInfo();
    let paymentIntentResponse;
    try {
      paymentIntentResponse = await paymentIntentApi.updatePaymentIntent({
        checkout_code: pressRelease.checkout_code === null || pressRelease.checkout_code?.error ? '' : pressRelease.checkout_code?.name,
        billing_contact_name: fields.billingContactName.value ? fields.billingContactName.value : pressRelease.submitter_name,
        billing_contact_email: fields.billingContactEmail.value ? fields.billingContactEmail.value : pressRelease.submitter_email,
        for_checking: {
          num_press_releases: numPressReleases,
          newsletter_add_ons: pressRelease.topic_newsletter_add_ons.join(', '),
          site_list: selectedSites.map((site) => site.site_id).join(', '),
          site_names: selectedSites.map((site) => site.site_name).join(', '),
          press_release_id: pressRelease.id,
          origin_site: (promoteSites.sitesList
            .find((site) => site.site_id === pressReleaseMetadata.current_site_pk)
          )?.site_domain,
          has_multimedia_add_on: hasMultimediaAddons,
        },
      });
    } catch (error) {
      setIsSubmitting(false);
      displayBackendErrors(error, null, setNonFieldError, null);
      return;
    }

    const { status } = paymentIntentResponse;
    if (!contactInfoIsValid || (status === 200 && !paymentInfoIsValid)) {
      setIsSubmitting(false);
      scrollToTopOrError(getErroringFieldNames());
    } else if (status === 200) {
      // Purchase price > 0, create stripe transaction
      const confirmCardPaymentResponse = await confirmCardPayment(
        paymentIntentResponse.data.client_secret,
        fields.billingContactName.value ? fields.billingContactName.value : pressRelease.submitter_name,
        fields.billingContactEmail.value ? fields.billingContactEmail.value : pressRelease.submitter_email,
      );
      setIsSubmitting(false);
      if (!confirmCardPaymentResponse.error) {
        setNonFieldError('');
        eventsHelper.handlePurchase();
        navigate(`${checkoutURL}/${token}/confirmation`);
      }
    } else if (status === 202) {
      // If there is no charge at checkout, the server returns a 202 response (price === 0)
      // Payment info doesn't need validation since price is $0
      // Activate press release without a charge
      setNonFieldError('');
      try {
        await paymentIntentApi.activatePressReleaseNoCharge({
          site_list: pressRelease.crosspost_sites,
          newsletter_add_ons: pressRelease.topic_newsletter_add_ons,
          checkout_code: pressRelease.checkout_code === null || pressRelease.checkout_code?.error ? '' : pressRelease.checkout_code?.name,
          billing_contact_name: fields.billingContactName.value ? fields.billingContactName.value : pressRelease.submitter_name,
          billing_contact_email: fields.billingContactEmail.value ? fields.billingContactEmail.value : pressRelease.submitter_email,
        });
        eventsHelper.handlePurchase();
        setIsSubmitting(false);
        navigate(`${checkoutURL}/${token}/confirmation`);
      } catch (error) {
        displayBackendErrors(error, null, setNonFieldError, null);
        setIsSubmitting(false);
      }
    }
  };
  return (
    <main id="skip-link-target">
      <div>
        <div className="row">
          <section className="columns">
            <IntroSection title="Finalize order and checkout">
              Review your order. Once you click &lsquo;Submit&lsquo; your press release will be published live immediately. If you scheduled a
              future publication date then your announcement will go live at the time you selected.
            </IntroSection>
          </section>
        </div>
        <div className="row payment-container">
          <section className="large-7 columns payment-billing-section">
            <MainSection className="payment-information" title="Payment information">
              <PaymentInfo
                validateCardNumber={handleCardNumberChange}
                validateCardExpiry={handleCardExpiryChange}
                validateCardCVC={handleCardCVCChange}
                cardFields={cardFields}
                amountOwed={totalPrice}
              />
            </MainSection>
            <MainSection className="billing-information" title="Billing contact information">
              <BillingContactInfo
                billingContactEmail={fields.billingContactEmail}
                billingContactName={fields.billingContactName}
                billingContactInfoNeeded={billingContactInfoNeeded}
                setBillingContactInfoNeededOnChange={setBillingContactInfoNeededOnChange}
                setField={setField}
                validateField={validateField}
              />
              {nonFieldError && <NonFieldErrorBanner setErrorCallback={setNonFieldError} prContactEmail={pressReleaseMetadata.pressrelease_contact_email} />}
              <DisclaimerAndSubmitButton handleSubmitClick={handleSubmitClick} />
            </MainSection>
          </section>
          <section className="large-5 columns price-summary-section">
            <PriceSummaryBox
              pressRelease={pressRelease}
              pressReleaseMetadata={pressReleaseMetadata}
              sitesList={promoteSites.sitesList}
              showCheckoutCodeInput
            />
          </section>
          {isSubmitting && <LoadingScreen isSubmitting={isSubmitting} />}
        </div>
      </div>
    </main>
  );
};

export default PaymentScreen;
